/**
 * Estado da sessão no navegador: usuário autenticado, permissões efetivas e
 * encerramento por inatividade (doc 19 §1 — o servidor também aplica a regra;
 * aqui é só a experiência). CP11: a sessão é o cookie HttpOnly (renovado pelo
 * próprio uso, no servidor); o frontend guarda só o token CSRF, em memória.
 *
 * Pacote 5 (26/10/2026 — inatividade de 120 min para todos os perfis):
 *  - as abas abertas compartilham a última atividade por BroadcastChannel
 *    (só instantes e o motivo do encerramento; nada da sessão, nada em
 *    localStorage/sessionStorage — CP11);
 *  - contam como uso: clique, ponteiro, teclado, roda do mouse, toque e
 *    rolagem (inclusive dentro de contêineres — escuta em captura);
 *  - uso real sem nenhuma requisição renova a sessão no servidor (uma consulta
 *    normal), para quem só lê/rola a tela não ser derrubado;
 *  - perto do fim, a aba CONFERE no servidor (GET /api/auth/sessao em segundo
 *    plano, que não renova) e só então mostra o aviso ou encerra — uma aba
 *    ociosa nunca derruba as outras que estão em uso;
 *  - aviso 5 minutos antes ("Sua sessão vai expirar"), com contagem regressiva.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, chamar, definirCsrf, definirOuvinteSessao, definirOuvinteUso, ErroApi } from './api.ts';
import { AvisoSessao } from './componentes/AvisoSessao.tsx';

export interface Eu {
  id: string;
  nome: string;
  email: string;
  perfil_nome: string;
  mfa_habilitado: boolean;
  permissoes: string[];
  sessao: { inatividade_min: number; absoluta_horas: number };
  csrf: string;
}

/** Resposta de GET /api/auth/sessao. */
export interface PrazosSessao {
  inatividade_min: number;
  absoluta_horas: number;
  segundos_ate_inatividade: number;
  segundos_ate_limite_absoluto: number;
  renovada: boolean;
}

interface ValorSessao {
  eu: Eu | null;
  carregando: boolean;
  aviso: string | null;
  entrar: () => Promise<void>;
  sair: (aviso?: string) => Promise<void>;
  pode: (acao: string, entidade: string) => boolean;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<ValorSessao | null>(null);

/** Canal entre as abas do HUB (mesma origem). Mensagens sem nenhum dado da sessão. */
const CANAL = 'inttrix-hub-sessao';
type Mensagem = { tipo: 'atividade'; em: number } | { tipo: 'renovada'; em: number } | { tipo: 'encerrada'; motivo: string | null };
/** O aviso aparece 5 min antes do fim (ou na metade, se a inatividade configurada for menor que 10 min). */
const AVISO_ANTES_MS = 5 * 60_000;
const VERIFICAR_A_CADA_MS = 5_000;
const DIFUNDIR_NO_MAXIMO_A_CADA_MS = 5_000;
const EVENTOS_DE_USO = ['mousedown', 'pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [eu, setEu] = useState<Eu | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Instante em que a sessão expira por inatividade, segundo o servidor — com o aviso aberto. */
  const [expiraEm, setExpiraEm] = useState<number | null>(null);
  const ultimaAtividade = useRef(Date.now());
  /** Última requisição normal (que renovou a inatividade no servidor), nesta ou em outra aba. */
  const ultimaRenovacao = useRef(Date.now());
  const proximaConferencia = useRef(0);
  const conferindo = useRef(false);
  const canal = useRef<BroadcastChannel | null>(null);
  const ultimaDifusao = useRef({ atividade: 0, renovada: 0 });

  const difundir = useCallback((m: Mensagem) => {
    try {
      canal.current?.postMessage(m);
    } catch {
      /* canal fechado */
    }
  }, []);

  /** Sessão terminou (nesta aba ou em outra): volta ao login com o motivo. */
  const encerrarLocal = useCallback((motivo: string | null) => {
    definirCsrf(null);
    setEu(null);
    setExpiraEm(null);
    setAviso(motivo);
  }, []);

  const recarregar = useCallback(async () => {
    const dados = await api.get<Eu>('/api/auth/eu');
    definirCsrf(dados.csrf);
    setEu(dados);
  }, []);

  useEffect(() => {
    canal.current = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CANAL) : null;
    if (canal.current) {
      canal.current.onmessage = (e: MessageEvent<Mensagem>) => {
        const m = e.data;
        if (m?.tipo === 'atividade') ultimaAtividade.current = Math.max(ultimaAtividade.current, m.em);
        else if (m?.tipo === 'renovada') ultimaRenovacao.current = Math.max(ultimaRenovacao.current, m.em);
        else if (m?.tipo === 'encerrada') encerrarLocal(m.motivo);
      };
    }
    definirOuvinteSessao((motivo) => {
      encerrarLocal(motivo);
      difundir({ tipo: 'encerrada', motivo });
    });
    definirOuvinteUso(() => {
      const agora = Date.now();
      ultimaRenovacao.current = agora;
      if (agora - ultimaDifusao.current.renovada >= DIFUNDIR_NO_MAXIMO_A_CADA_MS) {
        ultimaDifusao.current.renovada = agora;
        difundir({ tipo: 'renovada', em: agora });
      }
    });
    // Há sessão? Só o servidor sabe (o cookie é HttpOnly): 401 aqui apenas mostra o login.
    chamar<Eu>('GET', '/api/auth/eu', undefined, { silencioso: true })
      .then((dados) => {
        definirCsrf(dados.csrf);
        setEu(dados);
      })
      .catch(() => definirCsrf(null))
      .finally(() => setCarregando(false));
    return () => {
      canal.current?.close();
      canal.current = null;
      definirOuvinteUso(() => undefined);
    };
  }, [recarregar, encerrarLocal, difundir]);

  const entrar = useCallback(
    async () => {
      setAviso(null);
      ultimaAtividade.current = Date.now();
      proximaConferencia.current = 0;
      await recarregar();
    },
    [recarregar],
  );

  const sair = useCallback(
    async (motivo?: string) => {
      try {
        await chamar('POST', '/api/auth/logout', {}, { silencioso: true });
      } catch {
        /* sessão já encerrada no servidor */
      }
      encerrarLocal(motivo ?? null);
      difundir({ tipo: 'encerrada', motivo: motivo ?? null });
    },
    [encerrarLocal, difundir],
  );

  /** "Continuar conectado": requisição NORMAL, que renova a inatividade no servidor. */
  const continuar = useCallback(async () => {
    const agora = Date.now();
    ultimaAtividade.current = agora;
    difundir({ tipo: 'atividade', em: agora });
    try {
      await chamar('GET', '/api/auth/eu'); // 401 aqui já leva ao login (ouvinte da sessão)
      setExpiraEm(null);
      proximaConferencia.current = 0;
    } catch {
      /* falha de rede: o aviso continua e a próxima conferência decide */
    }
  }, [difundir]);

  // callbacks estáveis: o Modal refaz o foco quando aoFechar muda
  const aoContinuar = useCallback(() => void continuar(), [continuar]);
  const sairAgora = useCallback(() => void sair(), [sair]);

  // Atividade do usuário (compartilhada entre as abas) e encerramento por inatividade CONFIRMADO pelo servidor
  useEffect(() => {
    if (!eu) return;
    const inatividadeMs = eu.sessao.inatividade_min * 60_000;
    const avisoAntesMs = Math.min(AVISO_ANTES_MS, inatividadeMs / 2);
    const renovarAposMs = Math.min(AVISO_ANTES_MS, inatividadeMs / 4);
    const marcar = () => {
      const agora = Date.now();
      ultimaAtividade.current = agora;
      if (agora - ultimaDifusao.current.atividade >= DIFUNDIR_NO_MAXIMO_A_CADA_MS) {
        ultimaDifusao.current.atividade = agora;
        difundir({ tipo: 'atividade', em: agora });
      }
    };
    EVENTOS_DE_USO.forEach((e) => window.addEventListener(e, marcar, { passive: true }));
    // rolagem não "borbulha": em captura, pega também a rolagem dentro de tabelas, painéis e modais
    window.addEventListener('scroll', marcar, { passive: true, capture: true });

    const verificar = async () => {
      if (conferindo.current) return;
      const agora = Date.now();
      conferindo.current = true;
      try {
        // 1) uso real (nesta ou em outra aba) sem requisição recente: uma consulta normal renova no servidor
        if (ultimaAtividade.current > ultimaRenovacao.current && agora - ultimaRenovacao.current >= renovarAposMs) {
          await chamar<PrazosSessao>('GET', '/api/auth/sessao');
          setExpiraEm(null);
          proximaConferencia.current = 0;
          return;
        }
        // 2) houve uso há pouco: nada a avisar
        if (agora - ultimaAtividade.current < inatividadeMs - avisoAntesMs) {
          setExpiraEm(null);
          return;
        }
        // 3) perto do fim: quem decide é o servidor (consulta de segundo plano, que não renova)
        if (agora < proximaConferencia.current) return;
        const p = await chamar<PrazosSessao>('GET', '/api/auth/sessao', undefined, { fundo: true, silencioso: true });
        const restanteMs = Math.max(0, p.segundos_ate_inatividade * 1000);
        const depois = Date.now();
        if (restanteMs > avisoAntesMs) {
          // outra aba (ou outro uso) manteve a sessão: confere de novo só perto do novo fim
          setExpiraEm(null);
          proximaConferencia.current = depois + restanteMs - avisoAntesMs;
        } else {
          setExpiraEm(depois + restanteMs);
          proximaConferencia.current = depois + Math.min(15_000, Math.max(1_000, restanteMs));
        }
      } catch (e) {
        if (e instanceof ErroApi && e.status === 401) {
          // o servidor confirmou: a sessão acabou (e já foi revogada e registrada lá) — sem logout por conta própria
          const motivo = e.codigo === 'sessao_expirada' && /inatividade/i.test(e.message) ? `Sessão encerrada após ${eu.sessao.inatividade_min} minutos de inatividade.` : e.message;
          encerrarLocal(motivo);
          difundir({ tipo: 'encerrada', motivo });
        } else proximaConferencia.current = Date.now() + 15_000; // falha de rede: tenta de novo, nunca encerra sem confirmação
      } finally {
        conferindo.current = false;
      }
    };
    const timer = window.setInterval(() => void verificar(), VERIFICAR_A_CADA_MS);
    return () => {
      EVENTOS_DE_USO.forEach((e) => window.removeEventListener(e, marcar));
      window.removeEventListener('scroll', marcar, { capture: true });
      window.clearInterval(timer);
    };
  }, [eu, encerrarLocal, difundir]);

  const valor = useMemo<ValorSessao>(
    () => ({
      eu,
      carregando,
      aviso,
      entrar,
      sair,
      recarregar,
      pode: (acao, entidade) => Boolean(eu?.permissoes.includes(`${acao}:${entidade}`)),
    }),
    [eu, carregando, aviso, entrar, sair, recarregar],
  );
  return (
    <Contexto.Provider value={valor}>
      {children}
      {eu && expiraEm !== null && (
        <AvisoSessao
          expiraEm={expiraEm}
          ultimaAtividade={ultimaAtividade.current}
          absolutaHoras={eu.sessao.absoluta_horas}
          aoContinuar={aoContinuar}
          aoSair={sairAgora}
        />
      )}
    </Contexto.Provider>
  );
}

export function useSessao(): ValorSessao {
  const v = useContext(Contexto);
  if (!v) throw new Error('useSessao fora do ProvedorSessao');
  return v;
}
